import React from 'react';
import Graphic from '../../../objects/graphicsLibrary/graphic';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    makeStyles,
    IconButton,
    Typography,
    Tooltip,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import * as actions from '../../../store/actions';
import SnackBarType from '../../../objects/app/snackbar';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../../shared/hooks/useTypedSelector';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useTranslation } from 'react-i18next';
import transparentImg from '../../../img/transparent.png';
import { isMimeVector } from '../../../shared/Mimes';
import clsx from 'clsx';
import usePrices from '../../../shared/hooks/usePrices';

interface Props {
    graphic: Graphic | null;
    handleOnClose: () => void;
    handleOnDelete: () => void;
    onInsertGraphicFuncName: string | any;
    currentDesignId: string;
    customerId: number | null;
    guestId: number | null;
}

const useStyles = makeStyles((theme) => ({
    img: {
        maxWidth: '100%',
        maxHeight: '100%',
    },
    dialogTitle: {
        padding: 0,
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dialogTitleVector: {
        backgroundImage: `url(${transparentImg})`,
    },
    mime: {
        textTransform: 'uppercase',
    },
    deleteWrap: {
        position: 'absolute',
    },
    delete: {
        cursor: 'pointer',
        height: '20px',
        width: '20px',
    },
}));

export interface IIndexable {
    [key: string]: any;
}

const GraphicDetail: React.FC<Props> = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { data: productData } = useTypedSelector(
        (state) => state.od.loadProduct
    );
    const { displayPrice } = usePrices(productData?.currency);

    const {
        graphic,
        handleOnClose,
        handleOnDelete,
        onInsertGraphicFuncName,
        currentDesignId,
        customerId,
        guestId,
    } = props;

    const { loading: deleteGraphicLoading, success: deleteSuccess } =
        useTypedSelector((state) => state.graphicLibrary.deleteGraphic);

    const handleInsert = () => {
        if (window.hasOwnProperty(onInsertGraphicFuncName)) {
            (window as IIndexable)[onInsertGraphicFuncName](graphic);
            handleOnClose();
        } else {
            dispatch(
                actions.addSnackbar(
                    t('Graphic insert is not properly defined'),
                    SnackBarType.WARNING,
                    false
                )
            );
        }
    };

    React.useEffect(() => {
        if (deleteSuccess) {
            dispatch(
                actions.addSnackbar(
                    t('Graphic was deleted.'),
                    SnackBarType.SUCCESS,
                    false
                )
            );
            handleOnDelete();
            dispatch(actions.deleteGraphicReset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteSuccess]);

    const handleDelete = () => {
        if (graphic) {
            const currentUsedGraphics: number[] =
                window.getGraphicIdsFromCurrentDesign();
            if (currentUsedGraphics.includes(graphic.getId())) {
                dispatch(
                    actions.addSnackbar(
                        t(
                            'You can not delete this graphic. It is used in current design. Please delete it from your design and then delete it from your library.'
                        ),
                        SnackBarType.WARNING,
                        false
                    )
                );
            } else {
                dispatch(
                    actions.deleteGraphic(
                        graphic.getId(),
                        currentDesignId,
                        customerId,
                        guestId
                    )
                );
            }
        }
    };

    const loading = deleteGraphicLoading;
    return (
        <>
            {graphic && (
                <Dialog
                    fullWidth={true}
                    onClose={handleOnClose}
                    open={graphic.getId() > 0}
                    maxWidth={'xs'}
                >
                    <DialogTitle
                        className={clsx(
                            classes.dialogTitle,
                            isMimeVector(graphic.getMime()) &&
                                classes.dialogTitleVector
                        )}
                    >
                        <img
                            className={classes.img}
                            src={graphic.getBigImageUrl()}
                            title={graphic.getNameOrSource()}
                            alt={graphic.getNameOrSource()}
                        />
                    </DialogTitle>
                    <DialogContent>
                        <Grid container justifyContent="space-between">
                            <Grid item>
                                <Typography color="inherit">
                                    {graphic.getNameOrSource()}
                                    <Tooltip
                                        title={t<string>('Delete graphic')}
                                    >
                                        <IconButton
                                            onClick={handleDelete}
                                            className={classes.deleteWrap}
                                            size="small"
                                        >
                                            {loading && (
                                                <CircularProgress size={20} />
                                            )}
                                            {!loading &&
                                                graphic.getCustomers() && (
                                                    <DeleteOutlineIcon
                                                        className={
                                                            classes.delete
                                                        }
                                                    />
                                                )}
                                            {graphic.getPrice() !== 0 && (
                                                <span>
                                                    {displayPrice(
                                                        productData
                                                            ? graphic.getPrice() *
                                                                  (1 +
                                                                      productData.tax)
                                                            : graphic.getPrice()
                                                    )}
                                                </span>
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Typography>
                            </Grid>
                        </Grid>
                        {graphic.getDescription()}
                    </DialogContent>
                    <DialogActions>
                        <Grid container justifyContent="space-between">
                            <Grid item>
                                <Button
                                    onClick={handleOnClose}
                                    color="secondary"
                                    variant="outlined"
                                >
                                    {t('close')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    onClick={handleInsert}
                                    variant="contained"
                                >
                                    {t('Insert')}
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default GraphicDetail;
